import React, { useState } from 'react';
import { HelpCircle, Download } from 'lucide-react';
import { SidebarLayout, PageHeader, Card } from '../components/layout';
import { Button } from '../components/ui/button';
import { Select, Input } from '../components/ui/form';
import { useAuth } from '../hooks/use-auth';
import { useDivisionContext } from '../contexts/division-context';
import { useHelp } from '../contexts/HelpContext';
import { useWorkflowApproverReport, useDivisionWorkflowTypes } from '../hooks/use-division-workflows';

const DEFAULT_JSON_PATH = "$.Order_Attributes[?(@.Name == 'DODAACBEABESA')].Value";

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function WorkflowApproverReportPage() {
  const { isAuthenticated, isAdmin } = useAuth();
  const { openHelpPanel } = useHelp();
  const { selectedDivisionId, setSelectedDivisionId, divisions, currentDivision } = useDivisionContext();


  const [jsonPathInput, setJsonPathInput] = useState(DEFAULT_JSON_PATH);
  const [typeInput, setTypeInput] = useState('');
  const [nameContainsInput, setNameContainsInput] = useState('');
  const [queryJsonPath, setQueryJsonPath] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<{ type?: string; nameContains?: string }>({});

  const { data: report, isLoading, isFetching, error } = useWorkflowApproverReport(
    selectedDivisionId,
    queryJsonPath,
    appliedFilters,
    !!queryJsonPath
  );

  // Reflects the `Type` values actually saved on this division's workflows - there's no
  // canonical/enforced list for this free-text field (the workflow wizard, the division workflow
  // management modal, and the workflow templates page each offer their own different hardcoded
  // suggestions), so a hardcoded dropdown here would silently hide real workflows.
  const { data: workflowTypes } = useDivisionWorkflowTypes(selectedDivisionId);

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              Authentication Required
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              Please log in to access this report.
            </p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (!isAdmin) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              Access Denied
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              You don't have permission to access this report.
              <br />
              Required role: Admin
            </p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  const handleDivisionChange = (newDivisionId: string) => {
    setSelectedDivisionId(newDivisionId);
    setQueryJsonPath('');
  };

  const handleRunReport = () => {
    setAppliedFilters({
      type: typeInput || undefined,
      nameContains: nameContainsInput.trim() || undefined,
    });
    setQueryJsonPath(jsonPathInput.trim());
  };

  const handleExportCsv = () => {
    if (!report) return;

    const header = ['DodaacBeaBesa', ...report.columns];
    const lines = [header.map(csvEscape).join(',')];
    for (const row of report.rows) {
      const line = [row.routingRuleValue, ...report.columns.map((col) => row.approvers[col] || '')];
      lines.push(line.map(csvEscape).join(','));
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `workflow-approvers-${currentDivision?.code || selectedDivisionId}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <PageHeader
            title="Workflow Approver Report"
            subtitle="Approvers assigned to each step of every workflow using a given routing rule field"
            className="mb-0"
          />
          <button
            onClick={() => openHelpPanel('workflow')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-lg hover:bg-gray-50 dark:hover:bg-secondary-700 hover:border-blue-500 hover:text-blue-600 transition-colors"
          >
            <HelpCircle className="h-4 w-4" />
            <span>Help</span>
          </button>
        </div>

        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Division"
              value={selectedDivisionId}
              onChange={(e) => handleDivisionChange(e.target.value)}
            >
              <option value="">Choose a division...</option>
              {divisions.map((division) => (
                <option key={division.id} value={division.id}>
                  {division.name} {division.code ? `(${division.code})` : ''}
                </option>
              ))}
            </Select>

            <Input
              label="Routing Rule Field (JSONPath)"
              value={jsonPathInput}
              onChange={(e) => setJsonPathInput(e.target.value)}
              placeholder={DEFAULT_JSON_PATH}
            />

            <Select
              label="Workflow Type"
              value={typeInput}
              onChange={(e) => setTypeInput(e.target.value)}
              disabled={!selectedDivisionId}
            >
              <option value="">All Types</option>
              {(workflowTypes || []).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>

            <Input
              label="Workflow Name Contains"
              value={nameContainsInput}
              onChange={(e) => setNameContainsInput(e.target.value)}
              placeholder="e.g. Will Call"
            />

            <div className="flex gap-2 items-end md:col-span-2">
              <Button onClick={handleRunReport} disabled={!selectedDivisionId || !jsonPathInput.trim()}>
                Run Report
              </Button>
              {report && report.rows.length > 0 && (
                <Button variant="outline" onClick={handleExportCsv}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              )}
            </div>
          </div>
        </Card>


        {(isLoading || isFetching) && (
          <Card className="p-6 text-center text-secondary-600 dark:text-secondary-400">
            Loading report...
          </Card>
        )}

        {error && !isFetching && (
          <Card className="p-6 text-center text-red-600 dark:text-red-400">
            Failed to load the report. Please verify the routing rule field and try again.
          </Card>
        )}

        {report && !isFetching && (
          <Card className="p-0 overflow-x-auto">
            <div className="flex items-center justify-between px-6 pt-6">
              <div>
                <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
                  Workflow Approvers
                </h2>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  As of {new Date(report.generatedAt).toLocaleDateString()}
                </p>
              </div>
              <span className="text-sm text-secondary-500 dark:text-secondary-400">
                {report.rows.length} routing rule value{report.rows.length === 1 ? '' : 's'}
              </span>
            </div>

            <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700 mt-4">
              <thead className="bg-secondary-50 dark:bg-secondary-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    DodaacBeaBesa
                  </th>
                  {report.columns.map((col) => (
                    <th
                      key={col}
                      className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
                {report.rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={report.columns.length + 1}
                      className="px-6 py-8 text-center text-secondary-500 dark:text-secondary-400"
                    >
                      No workflows found with routing rules on this field.
                    </td>
                  </tr>
                ) : (
                  report.rows.map((row) => (
                    <tr key={`${row.workflowId}-${row.routingRuleValue}`}>
                      <td
                        className="px-6 py-3 text-sm font-medium text-secondary-900 dark:text-white whitespace-nowrap"
                        title={row.workflowName}
                      >
                        {row.routingRuleValue}
                      </td>
                      {report.columns.map((col) => (
                        <td
                          key={col}
                          className="px-6 py-3 text-sm text-secondary-700 dark:text-secondary-300 whitespace-pre-line"
                        >
                          {(row.approvers[col] || '').split('; ').filter(Boolean).join('\n')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </SidebarLayout>
  );
}
